<template>
    <div
        class="preview-inline"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
    >
        <slot></slot>
        <v-menu
            v-model="showMenu"
            :position-x="menuX"
            :position-y="menuY"
            absolute
            offset-y
            :close-on-content-click="false"
            max-width="800"
            min-width="700"
        >
            <v-card @mouseenter="onMenuEnter" @mouseleave="onMenuLeave">
                <v-card-text class="pa-0 pb-2">
                    <div v-if="loading" class="text-center py-4">
                        <v-progress-circular
                            indeterminate
                            color="primary"
                            size="20"
                        ></v-progress-circular>
                        <div class="text-caption mt-1">{{ $translate('loading') }}</div>
                    </div>
                    <div v-else-if="items && items.length > 0">
                        <div v-if="showInfos && receipt" class="pa-4 pb-0">
                            <div class="d-flex justify-space-between align-center">
                                <div>
                                    <router-link
                                        :to="{
                                            name: 'receipt',
                                            params: { receipt_id: receipt.receipt_id },
                                        }"
                                        class="text-subtitle-2 font-weight-bold text-decoration-none"
                                    >
                                        {{ receipt.receipt_id }}
                                    </router-link>
                                </div>
                                <v-chip small :color="$utils.getReceiptStatusColor(receipt)" :outlined="receipt.readonly">
                                    {{ $utils.getReceiptStatusText(receipt) }}
                                </v-chip>
                            </div>
                            <div class="d-flex justify-space-between mt-2 mb-2">
                                <div class="text-caption grey--text">
                                    <div v-if="receipt.stock_data" class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-warehouse</v-icon>
                                        {{ receipt.stock_data.name }}
                                    </div>
                                    <div v-if="receipt.provider_data" class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-store</v-icon>
                                        {{ receipt.provider_data.name }}
                                    </div>
                                </div>
                                <div class="text-caption grey--text text-right">
                                    <div v-if="receipt.receipt_date" class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                                        {{ $moment(receipt.receipt_date).format('ll') }}
                                    </div>
                                    <div class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-package-variant</v-icon>
                                        {{ receipt.items_count }} {{ $translate('items') }}
                                    </div>
                                </div>
                            </div>
                            <v-divider class="mb-2"></v-divider>
                        </div>
                        <div class="receipt-items-list" style="max-height: 400px; overflow-y: auto; overflow-x: auto;">
                            <v-simple-table dense class="no-wrap-table">
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th class="text-left">{{ $translate('upc') }}</th>
                                            <th class="text-left">{{ $translate('product') }}</th>
                                            <th class="text-left">{{ $translate('manufacturer') }}</th>
                                            <th class="text-right">{{ $translate('quantity') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="item in items" :key="item.id">
                                            <td class="text-left">
                                                {{ getProductUpc(item) }}
                                            </td>
                                            <td class="text-left font-weight-bold">
                                                {{ getProductName(item) }}
                                            </td>
                                            <td class="text-left">
                                                {{ getProductManufacturer(item) }}
                                            </td>
                                            <td class="text-right">
                                                <v-chip x-small outlined>
                                                    {{ item.count }}
                                                </v-chip>
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </div>
                    </div>
                    <div v-else-if="items && items.length === 0" class="text-body-2 pa-4">
                        {{ $translate('no_items_found') }}
                    </div>
                </v-card-text>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";

export default {
    name: "ReceiptPreview",
    mixins: [GlobalMixins],
    props: {
        receiptId: {
            type: [Number, String],
            required: true,
        },
        showInfos: {
            type: Boolean,
            default: true,
        },
        delay: {
            type: Number,
            default: 1000,
        },
    },
    data() {
        return {
            items: null,
            receipt: null,
            loading: false,
            hoverTimer: null,
            showMenu: false,
            menuX: 0,
            menuY: 0,
        };
    },
    methods: {
        onMouseEnter(event) {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
            }
            
            const rect = event.currentTarget.getBoundingClientRect();
            this.menuX = rect.left + 50;
            this.menuY = rect.bottom;
            
            this.hoverTimer = setTimeout(() => {
                this.showMenu = true;
                this.loadReceiptItems();
            }, this.delay);
        },
        onMouseLeave() {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
                this.hoverTimer = null;
            }
            
            this.hoverTimer = setTimeout(() => {
                this.showMenu = false;
            }, 200);
        },
        onMenuEnter() {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
                this.hoverTimer = null;
            }
        },
        onMenuLeave() {
            this.showMenu = false;
        },
        async loadReceiptItems() {
            if (this.items || this.loading) {
                return;
            }

            this.loading = true;

            try {
                const [itemsResponse, receiptResponse] = await Promise.all([
                    axios.get(`/api/receiptitems/?receipt=${this.receiptId}&limit=1000`),
                    axios.get(`/api/receipts/${this.receiptId}/`)
                ]);
                this.items = itemsResponse.data.results || [];
                this.receipt = receiptResponse.data;
            } catch (error) {
                console.error('Error loading receipt items:', error);
                this.items = [];
                this.receipt = null;
            } finally {
                this.loading = false;
            }
        },
        getProductUpc(item) {
            return item.product_data?.upc || '-';
        },
        getProductName(item) {
            return item.product_data?.name || '-';
        },
        getProductManufacturer(item) {
            return item.product_data?.manufacturer || '-';
        },
    },
    beforeDestroy() {
        if (this.hoverTimer) {
            clearTimeout(this.hoverTimer);
        }
    },
    i18n: {
        messages: {
            en: {
                loading: "Loading...",
                upc: "UPC",
                product: "Product",
                manufacturer: "Manufacturer",
                quantity: "Quantity",
                no_items_found: "Unable to load the list of receipt items",
                status: "Status",
                items: "items",
            },
            fr: {
                loading: "Chargement...",
                upc: "UPC",
                product: "Produit",
                manufacturer: "Fabricant",
                quantity: "Quantité",
                no_items_found: "Impossible de charger les lignes de réception",
                status: "Statut",
                items: "articles",
            },
        },
    },
};
</script>

<style scoped>
.preview-inline {
    display: inline;
}

.receipt-items-list {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.receipt-items-list::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}

.receipt-items-list::-webkit-scrollbar-track {
    background: transparent;
}

.receipt-items-list::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
}

.receipt-items-list::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.3);
}

.no-wrap-table >>> td,
.no-wrap-table >>> th {
    white-space: nowrap;
}

.no-wrap-table >>> table {
    table-layout: auto;
}

.no-wrap-table >>> tbody tr:hover {
    background-color: transparent !important;
}
</style>

